import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  TrendingUp,
  Plus,
  Sparkles,
  Loader2,
  BarChart3,
  ChefHat,
  Package,
  Trash2,
  Star,
  Layers,
} from 'lucide-react';
import { api, type PODProductType, type Recipe } from '../api/client';

const POD_PRODUCT_TYPES: { value: PODProductType; label: string }[] = [
  { value: 'all', label: 'All POD Products' },
  { value: 't-shirt', label: 'T-Shirts' },
  { value: 'hoodie', label: 'Hoodies' },
  { value: 'sweatshirt', label: 'Sweatshirts' },
  { value: 'tank-top', label: 'Tank Tops' },
  { value: 'mug', label: 'Mugs' },
  { value: 'poster', label: 'Posters' },
  { value: 'canvas', label: 'Canvas Prints' },
  { value: 'phone-case', label: 'Phone Cases' },
  { value: 'tote-bag', label: 'Tote Bags' },
  { value: 'sticker', label: 'Stickers' },
];

type Niche = {
  id: number;
  keyword: string;
  trend_score: number;
  status: string;
  source?: string;
  product_type?: PODProductType;
  recipe_id?: number | null;
  analysis?: string;
  created_at: string;
};

type RecommendedProduct = {
  type: string;
  difficulty: string;
  profitMargin: string;
};

const isApparelType = (type: string): boolean => {
  const apparelTypes = ['t-shirt', 'hoodie', 'sweatshirt', 'tank-top'];
  return apparelTypes.includes(type.toLowerCase());
};

const getTopRecommendedProduct = (niche: Niche): RecommendedProduct | null => {
  if (!niche.analysis) return null;
  try {
    const analysis = JSON.parse(niche.analysis);
    const products = analysis.recommendedProducts || [];
    if (products.length === 0) return null;
    return products[0];
  } catch {
    return null;
  }
};

export default function Research() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [newKeyword, setNewKeyword] = useState('');
  const [searchFilter, setSearchFilter] = useState(searchParams.get('search') || '');
  const [generatingIds, setGeneratingIds] = useState<Set<number>>(new Set());
  const [analyzingIds, setAnalyzingIds] = useState<Set<number>>(new Set());
  const [selectedRecipeId, setSelectedRecipeId] = useState<string>('');
  const [selectedProductType, setSelectedProductType] = useState<PODProductType>('all');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Sync search filter from URL params
  useEffect(() => {
    const searchFromUrl = searchParams.get('search');
    if (searchFromUrl) {
      setSearchFilter(searchFromUrl);
    }
  }, [searchParams]);

  const { data, isLoading } = useQuery({
    queryKey: ['niches'],
    queryFn: () => api.niches.list(),
  });

  const { data: recipesData } = useQuery({
    queryKey: ['recipes'],
    queryFn: () => api.recipes.list(),
  });

  const { data: settingsData } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.settings.get(),
  });

  const recipes: Recipe[] = recipesData?.recipes ?? [];
  const settings = settingsData?.settings;

  const addNiche = useMutation({
    mutationFn: (data: { keyword: string; product_type: PODProductType; recipe_id?: number }) =>
      api.niches.create({
        keyword: data.keyword,
        product_type: data.product_type,
        recipe_id: data.recipe_id,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['niches'] });
      setNewKeyword('');
    },
  });

  const deleteNiche = useMutation({
    mutationFn: (id: number) => api.niches.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['niches'] });
    },
  });

  const handleAnalyze = async (nicheId: number) => {
    setAnalyzingIds((prev) => new Set(prev).add(nicheId));
    try {
      await api.research.analyze(nicheId, settings?.default_research_model || undefined);
      queryClient.invalidateQueries({ queryKey: ['niches'] });
    } catch (error) {
      alert(`Analysis failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setAnalyzingIds((prev) => {
        const next = new Set(prev);
        next.delete(nicheId);
        return next;
      });
    }
  };

  const handleGenerate = async (niche: Niche) => {
    setGeneratingIds((prev) => new Set(prev).add(niche.id));
    try {
      await api.pipeline.generate({
        niche_id: niche.id,
        text_model: settings?.default_text_model || undefined,
        image_model: settings?.default_image_model || undefined,
        recipe_id: niche.recipe_id ? String(niche.recipe_id) : undefined,
        product_type: niche.product_type || 'all',
      });
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      alert(`Generation complete for "${niche.keyword}"! Check Inventory for results.`);
    } catch (error) {
      alert(
        `Generation failed for "${niche.keyword}": ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setGeneratingIds((prev) => {
        const next = new Set(prev);
        next.delete(niche.id);
        return next;
      });
    }
  };

  const allNiches: Niche[] = data?.niches ?? [];

  // Filter niches by search term
  const niches = searchFilter
    ? allNiches.filter((n) => n.keyword.toLowerCase().includes(searchFilter.toLowerCase()))
    : allNiches;

  // Update URL when search filter changes
  const handleSearchChange = (value: string) => {
    setSearchFilter(value);
    if (value) {
      setSearchParams({ search: value });
    } else {
      setSearchParams({});
    }
  };

  const getTrendColor = (score: number) => {
    if (score >= 0.7) return 'text-green-600 bg-green-100';
    if (score >= 0.4) return 'text-yellow-600 bg-yellow-100';
    return 'text-gray-600 bg-gray-100';
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Search className="w-8 h-8 text-gray-600" />
          <h1 className="text-3xl font-bold text-gray-900">Niche Research</h1>
        </div>
      </div>

      {/* Generation Settings */}
      <div className="card mb-8">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Package className="w-5 h-5 text-purple-600" />
          POD Generation Settings
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <ChefHat className="w-4 h-4" />
              Recipe (Optional)
            </label>
            <select
              value={selectedRecipeId}
              onChange={(e) => setSelectedRecipeId(e.target.value)}
              className="input w-full"
            >
              <option value="">Default POD Recipe</option>
              {recipes.map((recipe) => (
                <option key={recipe.id} value={recipe.id}>
                  {recipe.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Select a custom recipe for brainstorming</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Package className="w-4 h-4" />
              Product Type
            </label>
            <select
              value={selectedProductType}
              onChange={(e) => setSelectedProductType(e.target.value as PODProductType)}
              className="input w-full"
            >
              {POD_PRODUCT_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Filter concepts by POD product type</p>
          </div>
        </div>
      </div>

      {/* Add New Niche */}
      <div className="card mb-8">
        <h2 className="text-lg font-semibold mb-4">Add New Niche</h2>
        <div className="flex gap-4">
          <input
            type="text"
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            placeholder="Enter a niche keyword (e.g., Cottagecore Frogs)"
            className="input flex-1"
          />
          <button
            onClick={() =>
              newKeyword &&
              addNiche.mutate({
                keyword: newKeyword,
                product_type: selectedProductType,
                recipe_id: selectedRecipeId ? parseInt(selectedRecipeId) : undefined,
              })
            }
            disabled={!newKeyword || addNiche.isPending}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Niche
          </button>
        </div>
      </div>

      {/* Niches List */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Trending Niches</h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchFilter}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Filter niches..."
              className="input pl-9 pr-8 py-1.5 text-sm w-64"
            />
            {searchFilter && (
              <button
                onClick={() => handleSearchChange('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            )}
          </div>
        </div>
        {searchFilter && (
          <div className="mb-4 text-sm text-gray-500">
            Showing {niches.length} of {allNiches.length} niches matching "{searchFilter}"
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : niches.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {searchFilter ? (
              <>
                No niches matching "{searchFilter}".{' '}
                <button
                  onClick={() => handleSearchChange('')}
                  className="text-etsy-orange hover:underline"
                >
                  Clear filter
                </button>
              </>
            ) : (
              'No niches yet. Add one above to get started!'
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {niches.map((niche) => (
              <div
                key={niche.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div
                  className="flex items-center gap-4 flex-1 cursor-pointer"
                  onClick={() => niche.status === 'researched' && navigate(`/research/${niche.id}`)}
                >
                  <TrendingUp className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-medium flex items-center gap-2">
                      {niche.keyword}
                      {niche.product_type && niche.product_type !== 'all' && (
                        <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                          {POD_PRODUCT_TYPES.find((t) => t.value === niche.product_type)?.label ||
                            niche.product_type}
                        </span>
                      )}
                      {niche.recipe_id && (
                        <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
                          <ChefHat className="w-3 h-3" />
                          {recipes.find((r) => r.id === String(niche.recipe_id))?.name || 'Recipe'}
                        </span>
                      )}
                      {niche.status === 'researched' &&
                        (() => {
                          const topProduct = getTopRecommendedProduct(niche);
                          if (!topProduct) return null;
                          const isApparel = isApparelType(topProduct.type);
                          return (
                            <span
                              className={`px-2 py-0.5 text-xs rounded-full flex items-center gap-1 ${
                                isApparel
                                  ? 'bg-purple-100 text-purple-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}
                              title={`${topProduct.difficulty} difficulty, ${topProduct.profitMargin} margin`}
                            >
                              <Star className="w-3 h-3" />
                              Best: {topProduct.type}
                            </span>
                          );
                        })()}
                    </p>
                    <p className="text-sm text-gray-500 flex items-center gap-2 flex-wrap">
                      {(() => {
                        const source = niche.source ?? 'manual';
                        const sourceConfig: Record<
                          string,
                          { label: string; bg: string; text: string }
                        > = {
                          pinterest: {
                            label: '📌 Pinterest',
                            bg: 'bg-red-50',
                            text: 'text-red-600',
                          },
                          tiktok: { label: '🎵 TikTok', bg: 'bg-pink-50', text: 'text-pink-600' },
                          combined: {
                            label: '🔥 Multi-source',
                            bg: 'bg-orange-50',
                            text: 'text-orange-600',
                          },
                          'auto-scout': {
                            label: '🤖 Auto-Scout',
                            bg: 'bg-purple-50',
                            text: 'text-purple-600',
                          },
                          manual: { label: '✏️ Manual', bg: 'bg-gray-50', text: 'text-gray-600' },
                        };
                        const config = sourceConfig[source] || sourceConfig.manual;
                        return (
                          <span
                            className={`px-1.5 py-0.5 text-xs rounded ${config.bg} ${config.text}`}
                          >
                            {config.label}
                          </span>
                        );
                      })()}
                      <span>• {new Date(niche.created_at).toLocaleDateString()}</span>
                      {niche.status === 'researched' && (
                        <span className="text-blue-600">Click to view analysis</span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${getTrendColor(niche.trend_score)}`}
                  >
                    {(niche.trend_score * 100).toFixed(0)}% score
                  </span>
                  <span
                    className={`px-3 py-1 rounded-full text-sm ${
                      niche.status === 'researched'
                        ? 'bg-green-100 text-green-700'
                        : niche.status === 'ignored'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {niche.status}
                  </span>
                  {niche.status !== 'researched' && (
                    <button
                      onClick={() => handleAnalyze(niche.id)}
                      disabled={analyzingIds.has(niche.id)}
                      className="btn btn-secondary flex items-center gap-2"
                    >
                      {analyzingIds.has(niche.id) ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <BarChart3 className="w-4 h-4" />
                      )}
                      Analyze
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/batch?niche=${niche.id}`)}
                    className="btn btn-secondary flex items-center gap-2"
                    title="Generate multiple variants"
                  >
                    <Layers className="w-4 h-4" />
                    Batch
                  </button>
                  <button
                    onClick={() => handleGenerate(niche)}
                    disabled={generatingIds.has(niche.id)}
                    className="btn btn-primary flex items-center gap-2"
                  >
                    {generatingIds.has(niche.id) ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )}
                    Generate
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`Delete niche "${niche.keyword}"?`)) {
                        deleteNiche.mutate(niche.id);
                      }
                    }}
                    className="btn btn-secondary text-red-600 hover:bg-red-50"
                    title="Delete niche"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
